import React, { FC, useEffect, useMemo, useState } from 'react';
import { Button } from '@goldbely/explore-component-library';

import { useApiObject } from 'src/explore/hooks/useApiObject';
import { IMerchant, IMerchantReview } from 'src/explore/types/shoppe';
import { Spinner } from 'src/shoppe/elements/Spinner';

import { Reviews } from './organisms/Reviews';

import styles from './index.module.sass';

type ReviewsResponse = {
  data: IMerchantReview[];
  meta: {
    numberOfPages: number | null;
    pageNumber: number | null;
    pageSize: number | null;
    total: number | null;
  };
};

interface ProductReviewsProps {
  merchant: IMerchant;
  productId: number;
}

const ProductReviews: FC<ProductReviewsProps> = ({ merchant, productId }) => {
  const [ merchantReviews, setMerchantReviews ] = useState<IMerchantReview[]>([]);
  const [ merchantReviewsPage, setMerchantReviewsPage ] = useState<number>( 1 );

  const [ productReviews, setProductReviews ] = useState<IMerchantReview[]>([]);
  const [ productReviewsPage, setProductReviewsPage ] = useState<number>( 1 );

  const [ hasContent, setHasContent ] = useState<boolean>( false );
  const [ isScopedToProduct, setIsScopedToProduct ] = useState<boolean>( true );

  const { data: merchantReviewsData, loading: merchantReviewsLoading } =
    useApiObject<ReviewsResponse>( `/reviews?per_page=50&page=${merchantReviewsPage}`, {
      camelCaseKeys: true,
    });
  const { data: productReviewsData, loading: productReviewsLoading } =
    useApiObject<ReviewsResponse>(
      `/products/${productId}/reviews?per_page=50&page=${productReviewsPage}`,
      {
        camelCaseKeys: true,
      }
    );

  const isLoading = useMemo(
    () => merchantReviewsLoading || productReviewsLoading,
    [ merchantReviewsLoading, productReviewsLoading ]
  );

  const hasMorePages = ( data?: ReviewsResponse ) => {
    const { pageNumber, numberOfPages } = data?.meta ?? {};

    return pageNumber != null && numberOfPages != null && pageNumber < numberOfPages;
  };

  const hasMoreMerchantReviews = useMemo(
    () => hasMorePages( merchantReviewsData ),
    [ merchantReviewsData ]
  );

  const hasMoreProductReviews = useMemo(
    () => hasMorePages( productReviewsData ),
    [ productReviewsData ]
  );

  useEffect(() => {
    if ( merchantReviewsData?.data ) {
      setMerchantReviews(( state ) => state.concat( merchantReviewsData.data ));
    }
  }, [ merchantReviewsData ]);

  useEffect(() => {
    if ( productReviewsData?.data ) {
      if ( productReviewsData.data.length === 0 ) {
        setIsScopedToProduct( false );
      }

      setProductReviews(( state ) => state.concat( productReviewsData.data ));
    }
  }, [ productReviewsData ]);

  useEffect(() => {
    if ( !isLoading && ( merchantReviews.length > 0 || productReviews.length > 0 )) {
      setHasContent( true );
    }
  }, [ isLoading, merchantReviews, productReviews ]);

  return hasContent ? (
    <div className={styles.container}>
      <Reviews
        className={styles.reviews}
        merchant={merchant}
        reviews={merchantReviews}
        scopedReviews={productReviews}
        scopedCount={productReviewsData?.meta.total}
        totalCount={merchantReviewsData?.meta.total}
        onScoped={( state ) => setIsScopedToProduct( state )}
      />

      {(( !isScopedToProduct && hasMoreMerchantReviews ) ||
        ( isScopedToProduct && hasMoreProductReviews )) && (
        <div className={styles.more}>
          <Button
            icon={isLoading && <Spinner className={`${styles.spinner} spinner-border`} />}
            loading={isLoading}
            outline
            onClick={() =>
              isScopedToProduct
                ? setProductReviewsPage(( state ) => state + 1 )
                : setMerchantReviewsPage(( state ) => state + 1 )
            }
          >
            Show More Reviews
          </Button>
        </div>
      )}
    </div>
  ) : null;
};

export default ProductReviews;
